/**
 * 手入力の数量欄を読む（棚卸しの実数・在庫調整・拠点間移動・入荷数）。
 *
 * 🔴 これが必要な理由＝Polaris の `TextField type="integer"` は **DOM 上ただの text**
 * で、数値ガードは keypress にしか効かない。貼り付けと IME 確定は素通りするので、
 * 表計算やハンディ端末から `1,200` を貼れる。素の `Number.parseInt("1,200", 10)` は
 * **1** を返し `Number.isInteger` も通る＝棚卸しの「在庫へ反映」（入力値がそのまま
 * 在庫数になる）が 1,200 個の商品を 1 に上書きする。
 *
 * 🔑 CSV 取込は同じ入力を最初から正しく 1200 と読む（`csv_import._NUMERIC_NOISE`）。
 * 「CSV 経路では正しく手入力経路では間違う」形を作らないため、ここで同じ正規化を行う。
 */

/** 数値セルから落とす記号（桁区切り・空白・通貨）。backend の `_NUMERIC_NOISE` と対。 */
const NUMERIC_NOISE = [",", "，", " ", "　", "$", "¥", "￥", "€", "£"];

export type QuantityInput =
  | { kind: "value"; value: number }
  | { kind: "blank" }
  | { kind: "invalid" };

export interface ParseQuantityOptions {
  /** 在庫調整のように負数が業務上正当な欄で true にする（既定は拒否）。 */
  allowNegative?: boolean;
  /** 上限（入荷残数など）。超えたら invalid＝黙って丸めない。 */
  max?: number;
}

/**
 * 数量文字列を読む。`blank`（未入力）と `invalid`（読めない）を区別して返す＝
 * 呼び出し側が「触らない」と「エラーを出す」を取り違えないようにする。
 */
export function parseQuantityInput(
  raw: string,
  { allowNegative = false, max }: ParseQuantityOptions = {},
): QuantityInput {
  // NFKC で全角英数を半角へ（１２００ → 1200）。
  let text = raw.normalize("NFKC").trim();
  if (!text) return { kind: "blank" };
  for (const noise of NUMERIC_NOISE) text = text.split(noise).join("");
  if (!text) return { kind: "blank" };

  // 整数だけを受ける。小数・単位つき・記号混じりは「読めません」として返す
  // （切り捨てると利用者が入力したのと違う数量が黙って保存される）。
  if (!/^-?\d+$/.test(text)) return { kind: "invalid" };
  const value = Number(text);
  if (!Number.isSafeInteger(value)) return { kind: "invalid" };
  if (!allowNegative && value < 0) return { kind: "invalid" };
  if (max !== undefined && value > max) return { kind: "invalid" };
  return { kind: "value", value };
}
